import { clientsApi } from "#/api/clientsApi";
import { uploadApi } from "#/api/uploadApi";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import ImagePicker from "#/components/ui/ImagePicker";
import {
  type CreateClientPayload,
  SupportedAuthenticationMethod,
  UnitSystem,
} from "#/types/client";
import { ItemStatus, StatusType } from "#/types/items";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { logger } from "#/utils/logger";
import {
  Accordion,
  Button,
  ColorInput,
  Menu,
  NumberInput,
  Select,
  Switch,
  Tabs,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChevronDown,
  IconDeviceMobileCog,
  IconHeartRateMonitor,
  IconInfoCircle,
  IconPlus,
  IconRotateClockwise2,
  IconSparkles,
  IconTruckDelivery,
  IconUsers,
} from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { dashboardRoutes } from "#/routes/dashboardRoutes";
import {
  getDefaultVisibleColumnKeys,
  getTableColumnKeys,
  getTableConfig,
  getTableNames,
} from "#/config/tableColumnsConfig";
import { dateTimeFormatPresets } from "#/config/dateTimeFormats";
import ClientDevicesTable from "./components/ClientDevicesTable";
import ImportUsersDrawer from "./components/ImportUsersDrawer";
import UsersTable from "./components/UsersTable";
import AssistantTab from "./components/AssistantTab";
import ClientDataHealthTab from "./components/datahealth/ClientDataHealthTab";
import LogisticsPredictionTab from "./components/prediction/LogisticsPredictionTab";
import PrvEngineTab from "./components/prv/PrvEngineTab";
import { useAuth } from "#/hook/useAuth";
import { UserRole } from "#/types/user";
import { useTranslation } from "react-i18next";

const ClientDetailPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isTmaAdmin = user?.role === UserRole.ADMIN;
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>(searchParams.get("tab") || "details");

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (
      tabFromUrl &&
      (tabFromUrl === "details" ||
        tabFromUrl === "team" ||
        tabFromUrl === "devices" ||
        tabFromUrl === "assistant" ||
        tabFromUrl === "prv")
    ) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const [
    addUsersDrawerOpened,
    { open: openAddUsersDrawer, close: closeAddUsersDrawer },
  ] = useDisclosure(false);
  const [importMode, setImportMode] = useState<"manual" | "csv">("csv");
  const [refetchUsers, setRefetchUsers] = useState(0);

  const form = useForm<CreateClientPayload>({
    initialValues: {
      name: "",
      slug: "",
      color: "",
      supportedAuthenticationMethods: SupportedAuthenticationMethod.PASSWORD,
      address: "",
      contactEmail: "",
      allowedRoutes: undefined,
      visibleColumns: undefined,
      dateFormat: undefined,
      timeFormat: undefined,
      allowedStatuses: undefined,
      statusTypes: undefined,
      geofenceRadius: undefined,
      shipmentGapDeltaSeconds: undefined,
      unitSystem: undefined,
    },
    validate: {
      name: (value) => (value ? null : t("clients.validation.nameRequired")),
      slug: (value) => (value ? null : t("clients.validation.slugRequired")),
      color: (value) => (value ? null : t("clients.validation.colorRequired")),
      address: (value) => (value ? null : t("clients.validation.addressRequired")),
      contactEmail: (value) => {
        if (!value) {
          return t("clients.validation.contactEmailRequired");
        }
        return /^\S+@\S+$/.test(value) ? null : t("clients.validation.invalidEmail");
      },
    },
  });

  const { refetch } = useQuery({
    queryKey: ["client", id],
    queryFn: async () => {
      if (!id || id === "new") {
        return null;
      }

      try {
        const response = await clientsApi.getById(Number(id));
        form.setValues({
          name: response.data.name,
          color: response.data.color,
          slug: response.data.slug,
          supportedAuthenticationMethods:
            response.data.supportedAuthenticationMethods,
          address: response.data.address,
          contactEmail: response.data.contactEmail,
          allowedRoutes: response.data.allowedRoutes,
          visibleColumns: response.data.visibleColumns,
          dateFormat: response.data.dateFormat,
          timeFormat: response.data.timeFormat,
          allowedStatuses: response.data.allowedStatuses,
          statusTypes: response.data.statusTypes,
          geofenceRadius: response.data.geofenceRadius,
          shipmentGapDeltaSeconds: response.data.shipmentGapDeltaSeconds,
          unitSystem: response.data.unitSystem,
        });
        setLogoUrl(response.data.logo ?? null);
        form.resetDirty();
        return { client: response.data };
      } catch {
        throw new Error("No client returned");
      }
    },
    enabled: !!id && id !== "new",
    retry: false,
  });

  const handleSubmit = (values: typeof form.values) => {
    clientMutation.mutate(values);
  };

  const clientMutation = useMutation({
    mutationFn: async (values: CreateClientPayload) => {
      const payload = { ...values };
      if (logoFile) {
        const logoUrl = await uploadApi.upload(logoFile);
        payload.logo = logoUrl;
      }

      if (id !== "new") {
        return await clientsApi.update(Number(id), payload);
      } else {
        return await clientsApi.create(payload);
      }
    },
    onSuccess: (data) => {
      setLogoFile(null);
      if (id === "new") {
        void navigate(`/clients/${data.data.id}`);
      }
      showSuccessNotification({
        title: t("common.success"),
        message: id === "new" ? t("clients.createdSuccess") : t("clients.createdSuccess"),
        color: "green.5",
      });
      void refetch();
    },
    onError: (error) => {
      logger.error("ClientDetail handleSubmit error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const updateAllowedRoutesMutation = useMutation({
    mutationFn: async (allowedRoutes: string[] | undefined) => {
      if (id === "new") return;
      return await clientsApi.update(Number(id), { allowedRoutes });
    },
    onSuccess: () => {
      form.setFieldValue("allowedRoutes", form.values.allowedRoutes);
      form.resetDirty();
      void refetch();
    },
    onError: (error) => {
      logger.error("ClientDetail updateAllowedRoutes error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const getAllRoutePaths = (): string[] => {
    const paths: string[] = [];
    dashboardRoutes.forEach((route) => {
      paths.push(route.href);
      if (route.children) {
        route.children.forEach((child) => {
          paths.push(route.href + child.href);
        });
      }
    });
    return paths;
  };

  const isRouteAllowed = (routePath: string): boolean => {
    // If allowedRoutes is undefined or empty, all routes are allowed
    if (!form.values.allowedRoutes || form.values.allowedRoutes.length === 0) {
      return true;
    }
    return form.values.allowedRoutes.includes(routePath);
  };

  const handleRouteToggle = (routePath: string, checked: boolean) => {
    const isCurrentlyAllAllowed = !form.values.allowedRoutes || form.values.allowedRoutes.length === 0;

    let newAllowedRoutes: string[];

    if (isCurrentlyAllAllowed && !checked) {
      // If all routes are currently allowed and we're toggling OFF a route,
      // start with ALL routes, then remove the one being toggled
      newAllowedRoutes = getAllRoutePaths();
    } else {
      // Otherwise use existing allowedRoutes or empty array
      newAllowedRoutes = form.values.allowedRoutes
        ? [...form.values.allowedRoutes]
        : [];
    }

    if (checked) {
      // Add route if not already present
      if (!newAllowedRoutes.includes(routePath)) {
        newAllowedRoutes.push(routePath);
      }
    } else {
      // Remove route and all its children
      newAllowedRoutes = newAllowedRoutes.filter((route) => {
        // Remove the route itself
        if (route === routePath) return false;
        // Remove children of this route
        if (route.startsWith(routePath + "/")) return false;
        return true;
      });
    }

    form.setFieldValue("allowedRoutes", newAllowedRoutes);
    updateAllowedRoutesMutation.mutate(newAllowedRoutes);
  };

  // Column Visibility Logic
  const updateVisibleColumnsMutation = useMutation({
    mutationFn: async (visibleColumns: Record<string, string[]> | undefined) => {
      if (id === "new") return;
      return await clientsApi.update(Number(id), { visibleColumns });
    },
    onSuccess: () => {
      form.setFieldValue("visibleColumns", form.values.visibleColumns);
      form.resetDirty();
      void refetch();
    },
    onError: (error) => {
      logger.error("ClientDetail updateVisibleColumns error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  // Mirrors the web's rule (columnVisibilityUtils): no restriction = the
  // table's default set, so the switches show exactly what the page shows.
  const isColumnVisible = (tableName: string, columnKey: string): boolean => {
    const tableColumns = form.values.visibleColumns?.[tableName];
    if (!tableColumns || tableColumns.length === 0) {
      return getDefaultVisibleColumnKeys(tableName).includes(columnKey);
    }
    return tableColumns.includes(columnKey);
  };

  const handleColumnToggle = (tableName: string, columnKey: string, checked: boolean) => {
    const currentColumns = form.values.visibleColumns || {};
    const tableColumns = currentColumns[tableName] || [];

    // No config yet: the page is showing the table's default set, so start from it.
    const isUnrestricted = tableColumns.length === 0;

    let newTableColumns: string[];

    const required = getTableConfig(tableName)?.requiredColumn;
    if (required && columnKey === required && !checked) {
      return; // the identifier column cannot be hidden
    }

    if (isUnrestricted) {
      const defaults = getDefaultVisibleColumnKeys(tableName);
      newTableColumns = checked
        ? [...defaults.filter((key) => key !== columnKey), columnKey]
        : defaults.filter((key) => key !== columnKey);
    } else {
      newTableColumns = [...tableColumns];
      if (checked) {
        if (!newTableColumns.includes(columnKey)) {
          newTableColumns.push(columnKey);
        }
      } else {
        newTableColumns = newTableColumns.filter((key) => key !== columnKey);
      }
    }

    const newVisibleColumns = {
      ...currentColumns,
      [tableName]: newTableColumns,
    };

    form.setFieldValue("visibleColumns", newVisibleColumns);
    updateVisibleColumnsMutation.mutate(newVisibleColumns);
  };

  const handleShowAll = (tableName: string) => {
    const currentColumns = form.values.visibleColumns || {};
    const newVisibleColumns = {
      ...currentColumns,
      [tableName]: getTableColumnKeys(tableName),
    };
    form.setFieldValue("visibleColumns", newVisibleColumns);
    updateVisibleColumnsMutation.mutate(newVisibleColumns);
  };

  // An empty list means "unrestricted", so "Hide All" keeps the table's
  // required column: the smallest table that still identifies its rows.
  const handleHideAll = (tableName: string) => {
    const currentColumns = form.values.visibleColumns || {};
    const required = getTableConfig(tableName)?.requiredColumn;
    const newVisibleColumns = {
      ...currentColumns,
      [tableName]: required ? [required] : [],
    };
    form.setFieldValue("visibleColumns", newVisibleColumns);
    updateVisibleColumnsMutation.mutate(newVisibleColumns);
  };

  // Date & Time Format Logic
  const updateDateTimeFormatMutation = useMutation({
    mutationFn: async (payload: { dateFormat?: string; timeFormat?: string }) => {
      if (id === "new") return;
      return await clientsApi.update(Number(id), payload);
    },
    onSuccess: () => {
      form.setFieldValue("dateFormat", form.values.dateFormat);
      form.setFieldValue("timeFormat", form.values.timeFormat);
      form.resetDirty();
      void refetch();
    },
    onError: (error) => {
      logger.error("ClientDetail updateDateTimeFormat error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const handleFormatSelect = (preset: typeof dateTimeFormatPresets[0]) => {
    form.setFieldValue("dateFormat", preset.dateFormat);
    form.setFieldValue("timeFormat", preset.timeFormat);
    updateDateTimeFormatMutation.mutate({
      dateFormat: preset.dateFormat,
      timeFormat: preset.timeFormat,
    });
  };

  const getCurrentPresetId = (): string | undefined => {
    if (!form.values.dateFormat || !form.values.timeFormat) {
      return undefined;
    }
    const matchingPreset = dateTimeFormatPresets.find(
      (preset) =>
        preset.dateFormat === form.values.dateFormat &&
        preset.timeFormat === form.values.timeFormat
    );
    return matchingPreset?.id;
  };

  // Allowed Statuses Logic
  const updateAllowedStatusesMutation = useMutation({
    mutationFn: async (allowedStatuses: ItemStatus[] | undefined) => {
      if (id === "new") return;
      return await clientsApi.update(Number(id), { allowedStatuses });
    },
    onSuccess: () => {
      form.setFieldValue("allowedStatuses", form.values.allowedStatuses);
      form.resetDirty();
      void refetch();
    },
    onError: (error) => {
      logger.error("ClientDetail updateAllowedStatuses error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const isStatusAllowed = (status: ItemStatus): boolean => {
    // If allowedStatuses is undefined or empty, all statuses are allowed
    if (!form.values.allowedStatuses || form.values.allowedStatuses.length === 0) {
      return true;
    }
    return form.values.allowedStatuses.includes(status);
  };

  const handleStatusToggle = (status: ItemStatus, checked: boolean) => {
    const isCurrentlyAllAllowed = !form.values.allowedStatuses || form.values.allowedStatuses.length === 0;

    let newAllowedStatuses: ItemStatus[];

    if (isCurrentlyAllAllowed && !checked) {
      // If all statuses are currently allowed and we're toggling OFF a status,
      // start with ALL statuses, then remove the one being toggled
      newAllowedStatuses = Object.values(ItemStatus).filter((s) => s !== status);
    } else {
      // Otherwise use existing allowedStatuses or empty array
      newAllowedStatuses = form.values.allowedStatuses
        ? [...form.values.allowedStatuses]
        : [];
    }

    if (checked) {
      // Add status if not already present
      if (!newAllowedStatuses.includes(status)) {
        newAllowedStatuses.push(status);
      }
    } else {
      // Remove status
      newAllowedStatuses = newAllowedStatuses.filter((s) => s !== status);
    }

    form.setFieldValue("allowedStatuses", newAllowedStatuses);
    updateAllowedStatusesMutation.mutate(newAllowedStatuses);
  };

  // Status Type Logic
  const updateStatusTypesMutation = useMutation({
    mutationFn: async (statusTypes: Record<string, StatusType>) => {
      if (id === "new") return;
      return await clientsApi.update(Number(id), { statusTypes });
    },
    onSuccess: () => {
      form.resetDirty();
      void refetch();
    },
    onError: (error) => {
      logger.error("ClientDetail updateStatusTypes error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const statusTypeOptions = [
    { value: StatusType.INTERNAL, label: t("clients.statusType.internal") },
    { value: StatusType.EXTERNAL, label: t("clients.statusType.external") },
    { value: StatusType.EXITED, label: t("clients.statusType.exited") },
    { value: StatusType.MAINTENANCE, label: t("clients.statusType.maintenance") },
    { value: StatusType.AT_CUSTOMER, label: t("clients.statusType.atCustomer") },
  ];

  // Units Logic
  const updateUnitSystemMutation = useMutation({
    mutationFn: async (unitSystem: UnitSystem) => {
      if (id === "new") return;
      return await clientsApi.update(Number(id), { unitSystem });
    },
    onSuccess: () => {
      form.resetDirty();
      void refetch();
    },
    onError: (error) => {
      logger.error("ClientDetail updateUnitSystem error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const handleUnitSystemSelect = (unitSystem: UnitSystem) => {
    form.setFieldValue("unitSystem", unitSystem);
    updateUnitSystemMutation.mutate(unitSystem);
  };

  const handleStatusTypeChange = (status: ItemStatus, type: StatusType | null) => {
    const next: Record<string, StatusType> = { ...(form.values.statusTypes ?? {}) };
    if (type) {
      next[status] = type;
    } else {
      delete next[status];
    }
    form.setFieldValue("statusTypes", next);
    updateStatusTypesMutation.mutate(next);
  };

  return (
    <DetailPageLayout
      backTo="/clients"
      title={id !== "new" ? t("clients.clientDetails") : t("clients.createClient")}
      navRightSection={
        activeTab === "team" && id !== "new" ? (
          <Menu>
            <Menu.Target>
              <Button
                leftSection={<IconPlus size={18} />}
                rightSection={<IconChevronDown size={16} />}
                color="black"
                size="xs"
              >
                Add Users
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => {
                  setImportMode("manual");
                  openAddUsersDrawer();
                }}
              >
                Add Manually
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  setImportMode("csv");
                  openAddUsersDrawer();
                }}
              >
                Import from CSV
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : null
      }
    >
      <div className="h-full flex flex-col min-h-0">
        <Tabs defaultValue="details" className="w-full h-full flex flex-col" value={activeTab} onChange={(value) => {
          const newTab = value || "details";
          setActiveTab(newTab);
          setSearchParams({ tab: newTab }, { replace: true });
        }}>
          <Tabs.List className="flex-shrink-0">
            <Tabs.Tab value="details" leftSection={<IconInfoCircle size={16} />}>
              {t("clients.tabs.details")}
            </Tabs.Tab>
            {id !== "new" && (
              <Tabs.Tab value="devices" leftSection={<IconDeviceMobileCog size={16} />}>
                Devices
              </Tabs.Tab>
            )}
            {id !== "new" && (
              <Tabs.Tab value="team" leftSection={<IconUsers size={16} />}>
                {t("clients.tabs.teamMembers")}
              </Tabs.Tab>
            )}
            {id !== "new" && (
              <Tabs.Tab value="assistant" leftSection={<IconSparkles size={16} />}>
                AI Assistant
              </Tabs.Tab>
            )}
            {id !== "new" && isTmaAdmin && (
              <Tabs.Tab value="prv" leftSection={<IconRotateClockwise2 size={16} />}>
                {t("clients.tabs.prvEngine")}
              </Tabs.Tab>
            )}
            {id !== "new" && isTmaAdmin && (
              <Tabs.Tab value="prediction" leftSection={<IconTruckDelivery size={16} />}>
                {t("clients.tabs.logisticsPrediction")}
              </Tabs.Tab>
            )}
            {id !== "new" && isTmaAdmin && (
              <Tabs.Tab value="dataHealth" leftSection={<IconHeartRateMonitor size={16} />}>
                {t("clients.tabs.dataHealth")}
              </Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="details" pt="md" className="flex-1 overflow-auto min-h-0">
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <div className="w-full grid grid-cols-1 gap-4">
                <Accordion variant={"separated"} defaultValue={["basic"]} multiple>
                  <Accordion.Item value={"basic"}>
                    <Accordion.Control>
                      <div className="flex items-center justify-between w-full pr-4">
                        <span>{ t("common.basicDetails") }</span>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            color="black"
                            variant="outline"
                            type="reset"
                            disabled={!form.isDirty() && !logoFile}
                            onClick={() => {
                              form.reset();
                              setLogoFile(null);
                            }}
                            size="xs"
                          >
                            Clear
                          </Button>

                          <Button
                            color="black"
                            variant="filled"
                            loading={clientMutation.isPending}
                            disabled={!form.isDirty() && !logoFile}
                            onClick={() => {
                              form.onSubmit(handleSubmit)();
                            }}
                            size="xs"
                          >
                            {id === "new" ? t("common.create") : t("common.save")}
                          </Button>
                        </div>
                      </div>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <div className="w-full flex flex-col gap-4">
                        <div className="w-full grid grid-cols-2 gap-4">
                          <TextInput
                            label={t("clients.form.name")}
                            withAsterisk
                            placeholder={t("clients.form.namePlaceholder")}
                            {...form.getInputProps("name")}
                          />

                          <TextInput
                            label={t("clients.form.slug")}
                            withAsterisk
                            placeholder={t("clients.form.slugPlaceholder")}
                            {...form.getInputProps("slug")}
                          />

                          <Select
                            label={t("clients.form.authMethod")}
                            placeholder={t("clients.form.authMethodPlaceholder")}
                            searchable
                            data={Object.values(SupportedAuthenticationMethod).map(
                              (status) => ({
                                label: sanitize(status),
                                value: status,
                              })
                            )}
                            value={form.values.supportedAuthenticationMethods}
                            clearable
                            onChange={(_value) => {
                              form.setValues({
                                ...form.values,
                                supportedAuthenticationMethods:
                                  _value as SupportedAuthenticationMethod,
                              });
                            }}
                          />

                          <TextInput
                            label={t("clients.form.address")}
                            withAsterisk
                            placeholder={t("clients.form.addressPlaceholder")}
                            {...form.getInputProps("address")}
                          />

                          <TextInput
                            label={t("clients.form.contactEmail")}
                            withAsterisk
                            placeholder={t("clients.form.contactEmailPlaceholder")}
                            {...form.getInputProps("contactEmail")}
                          />

                          <ColorInput
                            label={t("clients.form.color")}
                            withAsterisk
                            placeholder={t("clients.form.colorPlaceholder")}
                            {...form.getInputProps("color")}
                          />
                        </div>

                        <ImagePicker
                          label={t("clients.form.logo")}
                          value={logoUrl}
                          file={logoFile}
                          onChange={setLogoFile}
                          alt="Logo"
                        />
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>

                  <Accordion.Item value={"operations"}>
                    <Accordion.Control>{ t("clients.sections.operations") }</Accordion.Control>
                    <Accordion.Panel>
                      <div className="w-full grid grid-cols-2 gap-4">
                        <NumberInput
                          label={t("clients.form.geofenceRadius")}
                          placeholder={t("clients.form.geofenceRadiusPlaceholder")}
                          description={t("clients.form.geofenceRadiusDescription")}
                          min={1}
                          allowDecimal={false}
                          allowNegative={false}
                          value={form.values.geofenceRadius ?? ""}
                          onChange={(value) => {
                            form.setFieldValue(
                              "geofenceRadius",
                              typeof value === "number" ? value : undefined,
                            );
                          }}
                        />

                        <NumberInput
                          label={t("clients.form.shipmentGapDelta")}
                          placeholder={t("clients.form.shipmentGapDeltaPlaceholder")}
                          description={t("clients.form.shipmentGapDeltaDescription")}
                          min={1}
                          allowDecimal={false}
                          allowNegative={false}
                          value={form.values.shipmentGapDeltaSeconds ?? ""}
                          onChange={(value) => {
                            form.setFieldValue(
                              "shipmentGapDeltaSeconds",
                              typeof value === "number" ? value : undefined,
                            );
                          }}
                        />
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>

                  {id !== "new" && (
                    <Accordion.Item value={"allowed_routes"}>
                      <Accordion.Control>{ t("clients.sections.allowedRoutes") }</Accordion.Control>
                      <Accordion.Panel>
                        <div className="w-full flex flex-col gap-3">
                          <div className="text-sm text-gray-600 mb-2">
                            Control which navigation routes this client can access. If no routes are selected, all routes will be available.
                          </div>
                          {dashboardRoutes.map((route) => {
                            const parentPath = route.href;
                            const isParentAllowed = isRouteAllowed(parentPath);

                            return (
                              <div key={route.href} className="flex flex-col gap-2">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <route.icon size={18} />
                                    <span className="font-medium">{t(route.label)}</span>
                                  </div>
                                  <Switch
                                    checked={isParentAllowed}
                                    onChange={(e) =>
                                      handleRouteToggle(parentPath, e.currentTarget.checked)
                                    }
                                    disabled={updateAllowedRoutesMutation.isPending}
                                  />
                                </div>

                                {route.children && route.children.length > 0 && (
                                  <div className="ml-8 flex flex-col gap-2">
                                    {route.children.map((child) => {
                                      const childPath = parentPath + child.href;
                                      const isChildAllowed = isRouteAllowed(childPath);

                                      return (
                                        <div
                                          key={child.href}
                                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                                        >
                                          <div className="flex items-center gap-2">
                                            <child.icon size={16} />
                                            <span className="text-sm">{t(child.label)}</span>
                                          </div>
                                          <Switch
                                            checked={isChildAllowed}
                                            onChange={(e) =>
                                              handleRouteToggle(
                                                childPath,
                                                e.currentTarget.checked
                                              )
                                            }
                                            disabled={
                                              !isParentAllowed ||
                                              updateAllowedRoutesMutation.isPending
                                            }
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  {id !== "new" && (
                    <Accordion.Item value={"column_visibility"}>
                      <Accordion.Control>{ t("clients.sections.columnVisibility") }</Accordion.Control>
                      <Accordion.Panel>
                        <div className="w-full flex flex-col gap-3">
                          <div className="text-sm text-gray-600 mb-2">
                            Control which columns each list page shows for this client. The switches
                            reflect exactly what the page shows: an unrestricted table shows its default
                            columns. The row identifier column can never be hidden.
                          </div>
                          <Tabs defaultValue={getTableNames()[0]}>
                            <Tabs.List>
                              {getTableNames().map((tableName) => (
                                <Tabs.Tab key={tableName} value={tableName}>
                                  {t(getTableConfig(tableName)?.navLabel ?? tableName)}
                                </Tabs.Tab>
                              ))}
                            </Tabs.List>

                            {getTableNames().map((tableName) => {
                              const config = getTableConfig(tableName);
                              if (!config) return null;

                              return (
                                <Tabs.Panel key={tableName} value={tableName} pt="md">
                                  <div className="w-full flex flex-col gap-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Button
                                        size="xs"
                                        variant="outline"
                                        color="black"
                                        onClick={() => handleShowAll(tableName)}
                                        disabled={updateVisibleColumnsMutation.isPending}
                                      >
                                        {t("common.showAll")}
                                      </Button>
                                      <Button
                                        size="xs"
                                        variant="outline"
                                        color="gray"
                                        onClick={() => handleHideAll(tableName)}
                                        disabled={updateVisibleColumnsMutation.isPending}
                                      >
                                        {t("common.hideAll")}
                                      </Button>
                                    </div>

                                    {config.columns.map((column) => (
                                      <div
                                        key={column.key}
                                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                                      >
                                        <span className="text-sm font-medium">
                                          {t(column.label)}
                                        </span>
                                        <Switch
                                          checked={isColumnVisible(tableName, column.key)}
                                          onChange={(e) =>
                                            handleColumnToggle(
                                              tableName,
                                              column.key,
                                              e.currentTarget.checked
                                            )
                                          }
                                          disabled={
                                            updateVisibleColumnsMutation.isPending ||
                                            column.key === config.requiredColumn
                                          }
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </Tabs.Panel>
                              );
                            })}
                          </Tabs>
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  {id !== "new" && (
                    <Accordion.Item value={"date_time_format"}>
                      <Accordion.Control>{ t("clients.sections.dateTimeFormat") }</Accordion.Control>
                      <Accordion.Panel>
                        <div className="w-full flex flex-col gap-3">
                          <div className="text-sm text-gray-600 mb-2">
                            Select how dates and times should be displayed throughout the application for this client. If no format is selected, the default format (DD/MM/YYYY HH:mm) will be used.
                          </div>
                          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            {dateTimeFormatPresets.map((preset) => {
                              const isSelected = getCurrentPresetId() === preset.id;
                              const isDefault = !form.values.dateFormat && !form.values.timeFormat && preset.id === "eu_24";

                              return (
                                <Button
                                  key={preset.id}
                                  variant={isSelected || isDefault ? "filled" : "outline"}
                                  color={isSelected || isDefault ? "black" : "gray"}
                                  className="h-auto p-3"
                                  onClick={() => handleFormatSelect(preset)}
                                  disabled={updateDateTimeFormatMutation.isPending}
                                >
                                  <div className="flex flex-col items-start w-full gap-1">
                                    <span className="text-sm font-semibold">{t(preset.label)}</span>
                                    <span className="text-xs opacity-80">{preset.example}</span>
                                  </div>
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  {id !== "new" && (
                    <Accordion.Item value={"units"}>
                      <Accordion.Control>{t("clients.sections.units")}</Accordion.Control>
                      <Accordion.Panel>
                        <div className="w-full flex flex-col gap-3">
                          <div className="text-sm text-gray-600 mb-2">
                            {t("clients.units.description")}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              { value: UnitSystem.METRIC, label: t("clients.units.metric") },
                              { value: UnitSystem.IMPERIAL, label: t("clients.units.imperial") },
                            ].map((option) => {
                              const isSelected = form.values.unitSystem === option.value;
                              const isDefault =
                                !form.values.unitSystem && option.value === UnitSystem.METRIC;

                              return (
                                <Button
                                  key={option.value}
                                  variant={isSelected || isDefault ? "filled" : "outline"}
                                  color={isSelected || isDefault ? "black" : "gray"}
                                  className="h-auto p-3"
                                  onClick={() => handleUnitSystemSelect(option.value)}
                                  disabled={updateUnitSystemMutation.isPending}
                                >
                                  <span className="text-sm font-semibold">{option.label}</span>
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  {id !== "new" && (
                    <Accordion.Item value={"allowed_statuses"}>
                      <Accordion.Control>{ t("clients.sections.allowedStatuses") }</Accordion.Control>
                      <Accordion.Panel>
                        <div className="w-full flex flex-col gap-3">
                          <div className="text-sm text-gray-600 mb-2">
                            Control which item statuses are available for this client. If no statuses are selected, all statuses will be available.
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            {Object.values(ItemStatus).map((status) => {
                              const isAllowed = isStatusAllowed(status);

                              return (
                                <div
                                  key={status}
                                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm"
                                >
                                  <span className="font-medium text-sm">{sanitizeItemStatusLabel(status)}</span>
                                  <Switch
                                    checked={isAllowed}
                                    onChange={(e) =>
                                      handleStatusToggle(status, e.currentTarget.checked)
                                    }
                                    disabled={updateAllowedStatusesMutation.isPending}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  {id !== "new" && (
                    <Accordion.Item value={"status_types"}>
                      <Accordion.Control>{t("clients.sections.statusType")}</Accordion.Control>
                      <Accordion.Panel>
                        <div className="w-full flex flex-col gap-3">
                          <div className="text-sm text-gray-600 mb-2">
                            {t("clients.statusType.description")}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {Object.values(ItemStatus).map((status) => (
                              <div
                                key={status}
                                className="flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm"
                              >
                                <span className="font-medium text-sm">{sanitizeItemStatusLabel(status)}</span>
                                <Select
                                  className="w-52"
                                  data={statusTypeOptions}
                                  value={form.values.statusTypes?.[status] ?? null}
                                  placeholder={t("clients.statusType.placeholder")}
                                  clearable
                                  disabled={updateStatusTypesMutation.isPending}
                                  onChange={(value) =>
                                    handleStatusTypeChange(status, value as StatusType | null)
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}
                </Accordion>
              </div>
            </form>
          </Tabs.Panel>

          {id !== "new" && (
            <Tabs.Panel value="devices" pt="md" className="flex-1 flex flex-col min-h-0">
              <ClientDevicesTable clientId={Number(id)} />
            </Tabs.Panel>
          )}

          {id !== "new" && (
            <Tabs.Panel value="team" pt="md" className="flex-1 flex flex-col min-h-0">
              <UsersTable clientId={Number(id)} key={refetchUsers} />
            </Tabs.Panel>
          )}

          {id !== "new" && (
            <Tabs.Panel value="assistant" pt="md" className="flex-1 overflow-auto min-h-0">
              <AssistantTab clientId={Number(id)} />
            </Tabs.Panel>
          )}

          {id !== "new" && isTmaAdmin && (
            <Tabs.Panel value="prv" pt="md" className="flex-1 overflow-auto min-h-0">
              <PrvEngineTab clientId={Number(id)} />
            </Tabs.Panel>
          )}

          {id !== "new" && isTmaAdmin && (
            <Tabs.Panel value="prediction" pt="md" className="flex-1 overflow-auto min-h-0">
              <LogisticsPredictionTab clientId={Number(id)} />
            </Tabs.Panel>
          )}

          {id !== "new" && isTmaAdmin && (
            <Tabs.Panel value="dataHealth" pt="md" className="flex-1 overflow-auto min-h-0">
              <ClientDataHealthTab clientId={Number(id)} />
            </Tabs.Panel>
          )}
        </Tabs>
      </div>

      <ImportUsersDrawer
        opened={addUsersDrawerOpened}
        onClose={closeAddUsersDrawer}
        clientId={Number(id)}
        mode={importMode}
        onSuccess={() => {
          setRefetchUsers(prev => prev + 1);
          closeAddUsersDrawer();
        }}
      />
    </DetailPageLayout>
  );
};

export default ClientDetailPage;
